import { useEffect, useState } from 'react';
import ActionPrompt from './ActionPrompt';
import ChoiceDialog from './ChoiceDialog';
import { TriggerGains } from './Amount';
import RowCard from './RowCard';
import AggressionActions from './AggressionPicker';
import PactActions from './PactPicker';
import DecisionPanel, { AuctionBox } from './DecisionPanel';
import EndTurnDialog from './EndTurnDialog';
import Amount, { CostLabel, IconText } from './Amount';
import { HoverCard, PrepareConfirm } from './PoliticalCards';
const TechnologyConverter = require('@tta/engine/Utilities/Converters/TechnologyConverter');
const { ActionGroups, GroupPhase } = require('@tta/engine/Moves/ActionCategories');
const { Triggers } = require('@tta/engine/ActionBar');

// The categories the action bar's buttons do, left out of the panel while the player uses static buttons
const BarCategories = new Set(Triggers.flatMap(trigger => trigger.categories));
const { MoveTypes, Phase } = require('@tta/engine/Moves/MoveTypes');
const { ShownName } = require('@tta/engine/Utilities/ShownNames');

const buttonClass = {
    [ActionGroups.Political]: 'tta-button political',
    [ActionGroups.Civil]: 'tta-button',
    [ActionGroups.Military]: 'tta-button military',
}

const groupTitles = {
    [ActionGroups.Political]: 'Political',
    [ActionGroups.Civil]: 'Civil',
    [ActionGroups.Military]: 'Military',
}

const groupColors = {
    [ActionGroups.Political]: '#6c4a97',
    [ActionGroups.Civil]: '#666',
    [ActionGroups.Military]: '#b13a2a',
}

// A single button that opens a dropdown of the category's targets, for actions with many rarely-wanted targets
// An action whose target is picked on the board (Destroy): its button starts picking (the board's cards that can be
// picked are outlined); then the card picked, the button to do it, and Cancel
function BoardPickAction({Category, GameEngine, Selection})
{
    const available = Category.options.filter(option => !option.disabled);
    const mine = Selection?.action === Category.id ? Selection : null;
    if (!mine)
    {
        return (
            <div className="tta-action-line" title={Category.title}>
                <button className={Category.neutral ? 'tta-button' : buttonClass[Category.group]} disabled={available.length === 0}
                    title={available.length === 0 ? Category.options[0]?.reason : 'Then pick it on your board'}
                    onClick={() => GameEngine.StartBoardAction(Category.id)}>
                    {Category.prompt}
                </button>
            </div>
        );
    }
    return (
        <div className="tta-action-line" style={{ alignItems: 'flex-end' }} title={Category.title}>
            <div className="tta-move-slot active" title="Click a card on your board to pick it">
                <div className="tta-move-slot-label">{Category.label}</div>
                <RowCard card={mine.picked[0] ? TechnologyConverter(mine.picked[0]) : null}/>
            </div>
            <button className={`${Category.neutral ? 'tta-button dark' : buttonClass[Category.group]} tta-button-centred`} disabled={!mine.tried?.ok}
                onClick={() => { GameEngine.StartBoardAction(null); GameEngine.submitMove(mine.tried.move); }}>
                {mine.picked[0] ? `${Category.label} ${ShownName(mine.picked[0])}` : 'Pick it on your board'}
                {/* (the action it spends: a civil action for a building, a military one for a unit) */}
                {mine.matched?.spends && <span className="tta-button-cost"> · <CostLabel cost={mine.matched.spends}/></span>}
            </button>
            <button className="tta-button" onClick={() => GameEngine.StartBoardAction(null)}>Cancel</button>
        </div>
    );
}

function PromptAction({Category, GameEngine})
{
    const [open, setOpen] = useState(false);
    const available = Category.options.filter(option => !option.disabled);
    return (
        <div className="tta-action-line" title={Category.title}>
            <button className={buttonClass[Category.group]} disabled={available.length === 0}
                title={available.length === 0 ? Category.options[0]?.reason : undefined}
                onClick={() => setOpen(true)}>
                {Category.prompt}
            </button>
            {open &&
                <ActionPrompt
                    Title={Category.prompt}
                    Options={available}
                    OnCancel={() => setOpen(false)}
                    OnConfirm={(option) => { setOpen(false); GameEngine.submitMove(option.move); }}/>}
        </div>
    );
}

// A category whose options are grouped (e.g. action cards): one button per group, playing it directly when there is
// a single way to use it and otherwise opening a choice between its options
function ChoiceActions({Category, GameEngine})
{
    // (the card each button plays, shown on hover: an action card)
    const cardOf = (id) => Category.id === 'playAction' ? GameEngine.gameState.GetCurrentPlayerState().civilHand.find(c => c?.id === id) : null;
    const [choosing, setChoosing] = useState(null);
    const groups = [];
    Category.options.forEach(option => {
        const group = groups.find(g => g.id === option.choiceOf.id);
        if (group)
        {
            group.options.push(option);
        } else {
            groups.push({ id: option.choiceOf.id, label: option.choiceOf.label, options: [option] });
        }
    });
    return (
        <div className="tta-action-line" title={Category.title}>
            {!Category.single && <span className="tta-action-title">{Category.label}:</span>}
            {groups.map(group => {
                const available = group.options.filter(option => !option.disabled);
                const only = group.options.length === 1 ? group.options[0] : null;
                return (
                    <HoverCard key={group.id} Card={cardOf(group.id)}>
                        <button className={buttonClass[Category.group]} disabled={available.length === 0}
                            title={available.length === 0 ? group.options[0].reason : only?.costNote}
                            onClick={() => only ? GameEngine.submitMove(only.move) : setChoosing(group)}>
                            {group.label}
                            {only?.cost && <span className="tta-button-cost"> · <CostLabel cost={only.cost}/></span>}
                        </button>
                    </HoverCard>
                );
            })}
            {choosing &&
                <ChoiceDialog
                    Title={choosing.label}
                    // (those that cannot be chosen now greyed out with the reason, e.g. a revolution with Breakthrough once a
                    // civil action is spent: still worth knowing it is there)
                    Options={choosing.options}
                    ButtonClass={buttonClass[Category.group]}
                    OnCancel={() => setChoosing(null)}
                    OnChoose={(option) => { setChoosing(null); GameEngine.submitMove(option.move); }}/>}
        </div>
    );
}

// A territory being auctioned, for everyone not asked about it now (the one asked to bid or pay has it in their
// decision): the territory, the bids so far and whom it waits on
function AuctionStatus({Auction, Players, PlayerColors, Decisions})
{
    if (!Auction || Decisions?.some(decision => decision.event === Auction.territory.name && !decision.waitingFor))
    {
        return null;
    }
    return <AuctionBox Auction={Auction} Players={Players.map(player => player.name)} PlayerColors={PlayerColors}/>;
}

// Other players' decisions still to come, less the auction's (its box says whom it waits on)
const NotAuction = (entries, auction) => (entries ?? []).filter(entry => entry.event !== auction?.territory.name);

// An aggression under way, for everyone, in a box like a decision's (and the auction's): who plays what against whom,
// and what it waits on
function AggressionStatus({Aggression, Players, PlayerColors, Decisions})
{
    // (nor when the viewer is the one deciding: their decision's own box says it all)
    if (!Aggression || Decisions?.some(decision => decision.event === Aggression.card.name && !decision.waitingFor))
    {
        return null;
    }
    // (each name in its player's color)
    const name = (player) => <b style={{ color: PlayerColors?.[player] }}>{Players[player].name}</b>;
    const aggressor = name(Aggression.aggressor);
    const defender = name(Aggression.defender);
    const choosing = Aggression.stage === 'choose';
    return (
        <div className="tta-decisions">
            <div className="tta-decision">
                <div className="tta-decision-title">{Aggression.card.name}</div>
                <div>{aggressor} plays it against {defender}: <Amount value={Aggression.aggressorStrength} kind="strength"/> vs <Amount value={Aggression.defenderStrength} kind="strength"/></div>
                <div className="tta-waiting">Waiting for {choosing ? aggressor : defender}.</div>
            </div>
        </div>
    );
}


// Trade Routes Agreement: a choice to pay part of the next cost in the other currency, once a turn; checking it changes
// the costs shown (and so what can be afforded)
function TradeRouteChoice({TradeRoute, Use, GameEngine})
{
    if (!TradeRoute)
    {
        return null;
    }
    const instead = TradeRoute.instead === 'food' ? 'food' : 'resource';
    const used = TradeRoute.left === 0;
    return (
        <label className="tta-trade-route" title={used ? 'Already used this turn' : `Once per turn (${TradeRoute.source})`}>
            <input type="checkbox" checked={Use && !used} disabled={used} onChange={(e) => GameEngine.setUseTradeRoute(e.target.checked)}/>
            {' '}<IconText text={`Pay 1 ${TradeRoute.payWith === 'food' ? 'food' : 'resource'} instead of 1 ${instead}`}/>
            <span className="tta-muted"> ({TradeRoute.source}{used ? ', used this turn' : ', once per turn'})</span>
        </label>
    );
}

// Other players' decisions still to come: the turn waits for blocking ones; the others can be waited for, or not
function WaitingOn({Entries})
{
    if (Entries.length === 0)
    {
        return null;
    }
    const blocking = Entries.filter(entry => entry.blocking);
    if (blocking.length > 0)
    {
        return <div className="tta-waiting">Waiting for <IconText text={blocking.map(e => `${e.player} to decide on ${e.subject ?? e.event}`).join(', ')}/>.</div>;
    }
    return (
        <div className="tta-waiting">
            Still to decide: <IconText text={Entries.map(e => `${e.player} (${e.subject ?? e.event})`).join(', ')}/>. You can carry on, or wait to see what they choose.
        </div>
    );
}

// Ending the turn: a quiet button while civil actions are still unused, a highlighted one once they are all spent;
// not while the player still has a decision to make (Blocked: why)
function EndTurnButton({EndPhase, CivilActionsLeft, Blocked, OnClick})
{
    return (
        <button className={`tta-button ${CivilActionsLeft > 0 ? 'end-early' : 'end-ready'}`} onClick={OnClick} disabled={!!Blocked}
            title={Blocked ?? (CivilActionsLeft > 0 ? 'You still have civil actions you could use' : 'All civil actions used')}>
            {EndPhase.label}
        </button>
    );
}

// A category with a button or two (counting a card with several ways to play it once; one asking first, e.g. Destroy a
// building, is one button), which shares its row
const IsSmall = (category) => !!category.prompt || new Set(category.options.map(option => option.choiceOf?.id ?? option)).size <= 2;

// One action category: its short name, then a button for each legal move with its cost
function ActionCategory({Category, GameEngine, Hand, Selection})
{
    // (an event being prepared: confirmed first, with the card shown)
    const [preparing, setPreparing] = useState(null);
    if (Category.ui === 'pact')
    {
        return <PactActions Category={Category} ButtonClass={buttonClass[Category.group]} GameEngine={GameEngine} Hand={Hand}/>;
    }
    if (Category.ui === 'aggression' || Category.ui === 'war')
    {
        return <AggressionActions Category={Category} ButtonClass={buttonClass[Category.group]} GameEngine={GameEngine} Hand={Hand}/>;
    }
    if (Category.ui === 'boardPick')
    {
        return <BoardPickAction Category={Category} GameEngine={GameEngine} Selection={Selection}/>;
    }
    if (Category.prompt)
    {
        return <PromptAction Category={Category} GameEngine={GameEngine}/>;
    }
    if (Category.options.some(option => option.choiceOf))
    {
        return <ChoiceActions Category={Category} GameEngine={GameEngine}/>;
    }
    return (
        <div className="tta-action-line" title={Category.title}>
            {!Category.single && <span className="tta-action-title">{Category.label}:</span>}
            {Category.options.map((option, index) =>
                <button key={index} className={buttonClass[Category.group]} disabled={option.disabled}
                    title={option.reason ?? (option.costNote && `Usual cost changed by: ${option.costNote}`)}
                    onClick={() => Category.id === 'prepareEvent' ? setPreparing(option) : GameEngine.submitMove(option.move)}>
                    <IconText text={option.label}/>{option.cost && <span className="tta-button-cost"> · <CostLabel cost={option.cost}/></span>}
                    {/* How cards changed the usual cost, e.g. "(−1 Moses)" */}
                    {option.costNote && <span className="tta-cost-note"> ({option.costNote})</span>}
                    <TriggerGains Gains={option.gains}/>
                </button>)}
            {preparing &&
                <PrepareConfirm Card={Hand.find(card => card.id === preparing.move.cardId)} Option={preparing}
                    OnCancel={() => setPreparing(null)}
                    OnConfirm={(option) => { setPreparing(null); GameEngine.submitMove(option.move); }}/>}
        </div>
    );
}

// The turn's own buttons: End turn (through the end-of-turn dialog), Undo, the resets, what is being sent or went wrong,
// and Resign. Only on the viewer's turn, while it is not held up by another player's decision. Shown in the action
// panel, or with the action bar (static buttons).
export function TurnButtons({GameViewModel, GameEngine})
{
    // Ending the turn always goes through the end-of-turn dialog (warnings, and discards when over the hand limit)
    const [confirmingEnd, setConfirmingEnd] = useState(false);
    // (the turn ended some other way, e.g. a failed send tried again by itself when the game changed: see Game.js)
    useEffect(() => { if (!GameViewModel.isViewersTurn) setConfirmingEnd(false); }, [GameViewModel.isViewersTurn]);
    const { endPhase, decisions, waitingOn } = GameViewModel.actionOptions;
    if (GameViewModel.finished || !GameViewModel.isViewersTurn || waitingOn?.some(entry => entry.blocking))
    {
        return null;
    }
    const currentPlayer = GameViewModel.playerViewModels[GameViewModel.currentPlayer];
    // Passing is one of the political options, so it sits in the Political row; ending the turn is here
    const passesPolitics = GameViewModel.phase === Phase.Political;
    // (saladin: Saladin's choice, made with ending the turn.) The discards go with ending the turn, and the dialog
    // stays open until the server has both: if sending fails, it says so there, with the turn and the choices made in
    // it as they were, to send again.
    const endTurn = async (discardIds, saladin) => {
        const end = saladin ? { ...endPhase.move, saladin } : endPhase.move;
        const result = await GameEngine.submitTogether(discardIds.length > 0
            ? [{ type: MoveTypes.DiscardMilitaryCards, player: endPhase.move.player, cardIds: discardIds }, end] : [end]);
        // (or an earlier send went through instead: said so in a message of its own, see Game.js)
        if (result.ok || result.earlier)
        {
            setConfirmingEnd(false);
        }
    };
    // Going back to the turn from the dialog (after a failed send, too: the turn is as it was)
    const keepPlaying = () => {
        GameEngine.dismissSendFailure();
        setConfirmingEnd(false);
    };
    // Leaving the game, after a confirmation: a political action, so only in the player's own political phase
    const resigner = GameViewModel.currentPlayer;
    const resign = () => {
        const name = GameViewModel.playerViewModels[resigner].name;
        if (window.confirm(`Resign the game as ${name}? This is your political action, and you leave the game for good.`))
        {
            GameEngine.submitMove({ type: MoveTypes.Resign, player: resigner });
        }
    };
    return (
        <div className="tta-turn-buttons" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            {endPhase && !passesPolitics && <EndTurnButton EndPhase={endPhase}
                CivilActionsLeft={GameViewModel.endTurnPreview?.civilActionsLeft ?? 0} OnClick={() => setConfirmingEnd(true)}
                Blocked={decisions?.find(d => d.player === GameViewModel.currentPlayer) ? `Decide on ${decisions.find(d => d.player === GameViewModel.currentPlayer).event} first` : null}/>}
            {confirmingEnd && GameViewModel.endTurnPreview &&
                <EndTurnDialog Preview={GameViewModel.endTurnPreview} MilitaryHand={currentPlayer.militaryHand} GameViewModel={GameViewModel}
                    Sending={GameViewModel.waiting} Failure={GameViewModel.sendFailure?.endTurn ? GameViewModel.sendFailure : null}
                    OnCancel={keepPlaying} OnConfirm={endTurn}/>}
            <button className="tta-button" disabled={!GameViewModel.canUndo} onClick={() => GameEngine.undo()}>Undo</button>
            {/* In the political phase, one reset; in the action phase, back to its start, and back to the political
                phase while passing it can still be undone */}
            {passesPolitics
                ? <button className="tta-button" disabled={!GameViewModel.canUndo} onClick={() => GameEngine.resetTurn(true)}>Reset turn</button>
                : <button className="tta-button" disabled={!GameViewModel.resets.actionPhase} onClick={() => GameEngine.resetTurn()}
                    title="Back to the start of the action phase">Reset action phase</button>}
            {!passesPolitics && GameViewModel.resets.politicalPhase &&
                <button className="tta-button" onClick={() => GameEngine.resetTurn(true)}
                    title="Back to the political phase, to play it again">Reset to political phase</button>}
            {/* A move on its way to the server */}
            {GameViewModel.waiting && <span className="tta-muted" style={{ marginLeft: '8px' }}>Sending…</span>}
            {GameViewModel.lastError && <span style={{ color: '#b13a2a', fontWeight: 'bold', marginLeft: '8px' }}>{GameViewModel.lastError}</span>}
            {passesPolitics &&
                <button className="tta-button" style={{ marginLeft: 'auto' }} onClick={resign} title="Leave the game for good">Resign</button>}
        </div>
    );
}

// Passing the political phase (with Julius Caesar's second political action still to play: ending it), in the action
// panel's Political row, or in the action bar's box (static buttons), where it is greyed out when it isn't the viewer's
// turn
export function PassPoliticalButton({GameViewModel, GameEngine, ClassName})
{
    const { endPhase, waitingOn } = GameViewModel.actionOptions;
    const usable = endPhase && GameViewModel.isViewersTurn && !GameViewModel.finished && !waitingOn?.some(entry => entry.blocking);
    const title = !usable ? 'Not your turn'
        : GameViewModel.caesarSecond ? (GameViewModel.caesarOncePerGame ? "End the political phase, keeping Julius Caesar's second political action for later" : "End the political phase without Julius Caesar's second political action")
        : undefined;
    return (
        <button className={ClassName} disabled={!usable} onClick={() => GameEngine.submitMove(endPhase.move)} title={title}>
            {GameViewModel.caesarSecond ? 'End political phase' : endPhase?.label ?? 'Pass political phase'}
        </button>
    );
}

// The political phase in the action bar's box (static buttons), beside its own buttons for the cards played (preparing
// an event, aggressions, wars, pacts): the other political actions as the panel has them (cancelling a pact, leaders'),
// then passing. On someone else's turn, only the pass button, greyed out.
export function PoliticalActions({GameViewModel, GameEngine})
{
    const pass = <PassPoliticalButton GameViewModel={GameViewModel} GameEngine={GameEngine} ClassName="tta-action-bar-button pass"/>;
    const { waitingOn } = GameViewModel.actionOptions;
    if (!GameViewModel.isViewersTurn || GameViewModel.finished || waitingOn?.some(entry => entry.blocking))
    {
        return pass;
    }
    const currentPlayer = GameViewModel.playerViewModels[GameViewModel.currentPlayer];
    const byBar = Triggers.filter(trigger => trigger.phase === Phase.Political).flatMap(trigger => trigger.categories);
    const categories = GameViewModel.actionOptions.categories
        .filter(category => category.group === ActionGroups.Political && !category.onBoard && category.options.length > 0 && !byBar.includes(category.id));
    return (
        <>
            <div className="tta-action-categories tta-action-bar-political-actions">
                {GameViewModel.caesarSecond &&
                    <div className="tta-action-category small"><span className="tta-caesar-note">Julius Caesar: you may play another political action{GameViewModel.caesarOncePerGame ? ' (once per game)' : ''}</span></div>}
                {categories.map(category =>
                    <div key={category.id} className={`tta-action-category${IsSmall(category) ? ' small' : ''}`}>
                        <ActionCategory Category={category} GameEngine={GameEngine} Hand={currentPlayer.militaryHand} Selection={GameViewModel.boardSelection}/>
                    </div>)}
            </div>
            {pass}
        </>
    );
}

// A group's categories in order, those picked on the board (Destroy) last
const InGroup = (categories, group) => categories.filter(category => category.group === group)
    .sort((x, y) => (x.ui === 'boardPick') - (y.ui === 'boardPick'));

// The actions available right now (only categories with at least one legal move), undo controls and this turn's moves.
// StaticButtons: the player uses the action bar, so the actions it has buttons for are not listed here.
// NewUI: the Trade Routes Agreement's choice is made on its card instead (see PlayerArea's PactsBox)
function TurnControls({GameViewModel, GameEngine, PlayerColors, StaticButtons, NewUI})
{
    const { endPhase } = GameViewModel.actionOptions;
    const currentPlayer = GameViewModel.playerViewModels[GameViewModel.currentPlayer];
    const { decisions, waitingOn } = GameViewModel.actionOptions;
    const decisionPanel = decisions?.length > 0 &&
        <DecisionPanel Decisions={decisions} Frozen={GameViewModel.frozen} GameEngine={GameEngine}
            ShowNames={GameViewModel.viewer === null} Error={GameViewModel.lastError}
            MilitaryHands={GameViewModel.playerViewModels.map(player => player.militaryHand)}
            BoardSelection={GameViewModel.boardSelection}
            Auction={GameViewModel.auction} Players={GameViewModel.playerViewModels.map(player => player.name)} PlayerColors={PlayerColors}
            LouvreTokens={GameViewModel.playerViewModels.map(player => player.louvreTokens)}/>;
    const auction = <AuctionStatus Auction={GameViewModel.auction} Players={GameViewModel.playerViewModels} PlayerColors={PlayerColors} Decisions={decisions}/>;
    const others = NotAuction(waitingOn, GameViewModel.auction);
    // The game is over: nothing more to do (the result is shown above)
    if (GameViewModel.finished)
    {
        return null;
    }
    // Someone else's turn: only decisions to make, or nothing to do but watch (the turn held up by another player's
    // decision: waiting on them, not on the current player). Just waiting for the current player to play takes no room:
    // the phase line above already says whose turn it is.
    if (!GameViewModel.isViewersTurn)
    {
        return (
            <>
                {decisionPanel || (GameViewModel.auction
                    ? <div className="tta tta-left">{auction}<WaitingOn Entries={others}/></div>
                    : GameViewModel.aggression
                    ? <div className="tta tta-left"><AggressionStatus Aggression={GameViewModel.aggression} Players={GameViewModel.playerViewModels} PlayerColors={PlayerColors} Decisions={decisions}/></div>
                    : waitingOn?.some(entry => entry.blocking)
                    ? <div className="tta tta-left"><WaitingOn Entries={waitingOn}/></div>
                    : null)}
            </>
        );
    }
    // The viewer's turn, held up by another player's decision (e.g. their bid for a territory): nothing to do but their
    // own decisions, if any, until it is made
    if (waitingOn?.some(entry => entry.blocking))
    {
        return (
            <div className="tta tta-left" style={{ padding: '4px 8px 8px' }}>
                {decisionPanel}
                {auction}
                <AggressionStatus Aggression={GameViewModel.aggression} Players={GameViewModel.playerViewModels} PlayerColors={PlayerColors} Decisions={decisions}/>
                {/* (less the aggression's: its box says whom it waits on) */}
                <WaitingOn Entries={others.filter(entry => entry.event !== GameViewModel.aggression?.card.name)}/>
            </div>
        );
    }
    // Card row cards are taken by clicking them, so they are not listed here; with static buttons, neither are the
    // political actions (they are in the action bar's box) nor those the bar has buttons for
    const categories = GameViewModel.actionOptions.categories
        .filter(category => !category.onBoard && category.options.length > 0
            && !(StaticButtons && (BarCategories.has(category.id) || category.group === ActionGroups.Political)));
    // Every group of the current phase keeps its row, even with nothing available, so the panel does not jump around
    // (with static buttons, only those with something left in them, e.g. the Louvre Museum, and without their headings:
    // the bar is what stays put)
    const groups = Object.values(ActionGroups).filter(group => GroupPhase[group] === GameViewModel.phase)
        .filter(group => !StaticButtons || categories.some(category => category.group === group && !category.neutral));
    // Passing is one of the political options, so it sits in the Political row; ending the turn stays at the top
    const passesPolitics = GameViewModel.phase === Phase.Political;
    // With static buttons there is often nothing left for this panel: then it takes no room at all
    const tradeRoute = GameViewModel.phase !== 'Political' && !NewUI && GameViewModel.tradeRoute;
    if (StaticButtons && !decisionPanel && !GameViewModel.auction && !GameViewModel.aggression && others.length === 0
        && !tradeRoute && groups.length === 0)
    {
        return null;
    }

    return (
        <div className="tta tta-left" style={{ padding: '4px 8px 8px' }}>
            {decisionPanel}
            {auction}
            <AggressionStatus Aggression={GameViewModel.aggression} Players={GameViewModel.playerViewModels} PlayerColors={PlayerColors} Decisions={decisions}/>
            <WaitingOn Entries={others}/>
            {/* (Paying with the pact's pool only comes up in the action phase) */}
            {GameViewModel.phase !== 'Political' && !NewUI && <TradeRouteChoice TradeRoute={GameViewModel.tradeRoute} Use={GameViewModel.useTradeRoute} GameEngine={GameEngine}/>}
            {/* (with static buttons, these are with the action bar) */}
            {!StaticButtons && <TurnButtons GameViewModel={GameViewModel} GameEngine={GameEngine}/>}
            <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                {/* The groups side by side (civil, then military to its right; under it when there is no room), each with its
                    categories laid out across it */}
                <div className="tta-action-groups">
                    {groups.map(group =>
                        <div key={group} className="tta-action-group">
                            {!StaticButtons && <span className="tta-action-group-title" style={{ color: groupColors[group] }}>{groupTitles[group]}</span>}
                            {/* A row per category (its buttons wrapping within it); small ones (a button or two, e.g. Increase
                                Population, Government) share a row */}
                            <div className="tta-action-categories">
                                {/* (with static buttons, passing is in the action bar's box) */}
                                {group === ActionGroups.Political && passesPolitics && endPhase && !StaticButtons &&
                                    <div className="tta-action-category small">
                                        <PassPoliticalButton GameViewModel={GameViewModel} GameEngine={GameEngine} ClassName={buttonClass[group]}/>
                                    </div>}
                                {group === ActionGroups.Political && GameViewModel.caesarSecond &&
                                    <div className="tta-action-category small"><span className="tta-caesar-note">Julius Caesar: you may play another political action{GameViewModel.caesarOncePerGame ? ' (once per game)' : ''}</span></div>}
                                {/* (actions picked on the board, Destroy, last; while one is being picked, in its own box below) */}
                                {InGroup(categories, group).filter(category => !category.neutral && GameViewModel.boardSelection?.action !== category.id).map(category =>
                                    <div key={category.id} className={`tta-action-category${IsSmall(category) ? ' small' : ''}`}>
                                        <ActionCategory Category={category} GameEngine={GameEngine} Hand={currentPlayer.militaryHand} Selection={GameViewModel.boardSelection}/>
                                    </div>)}
                                {InGroup(categories, group).filter(category => !category.neutral && GameViewModel.boardSelection?.action === category.id).map(category =>
                                    <div key={category.id} className="tta-board-pick-box">
                                        <ActionCategory Category={category} GameEngine={GameEngine} Hand={currentPlayer.militaryHand} Selection={GameViewModel.boardSelection}/>
                                    </div>)}
                            </div>
                        </div>)}
                </div>
            </div>
            {/* Actions of no one group (Destroy: civil or military by what is picked), in a row of their own below them all */}
            {categories.filter(category => category.neutral && groups.includes(category.group)).map(category =>
                <div key={category.id} className={`tta-action-other${GameViewModel.boardSelection?.action === category.id ? ' tta-board-pick-box' : ''}`}>
                    <ActionCategory Category={category} GameEngine={GameEngine} Hand={currentPlayer.militaryHand} Selection={GameViewModel.boardSelection}/>
                </div>)}
        </div>
    );
}

export default TurnControls;